import { Controller, Control, FieldErrors } from 'react-hook-form';
import { LoanFormData } from '@/schemas/loanSchema';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import USFormattedNumberInput from '@/components/ui/USFormattedNumberInput';

interface Props {
  control: Control<LoanFormData>;
  errors: FieldErrors<LoanFormData>;
}

// Normalize any backend/frontend date value to `YYYY-MM-DD` string for the DatePicker.
// Accepts either a string (e.g. '2025-01-31', '2025-01-31T00:00:00', '01/31/2025')
// or a Date instance and returns a safe value for the input, or '' if invalid/empty.
function normalizeDateValue(value: unknown): string {
  if (!value) return '';

  // If we already have a Date, format directly
  if (value instanceof Date) {
    // Guard against invalid Date
    if (Number.isNaN(value.getTime())) return '';
    return value.toISOString().split('T')[0];
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';

    // Try to parse the string into a Date and normalize
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      // As a fallback, if the backend already gives 'YYYY-MM-DD', just use it
      // and let the browser validate. This covers already-correct values.
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      return '';
    }
    return parsed.toISOString().split('T')[0];
  }

  return '';
}

export const KeyDatesSection = ({ control }: Props) => (
  <div className="p-8 mb-8" data-test-id="key-dates-container">
    <h3 className="section-title" data-test-id="key-dates-section">
      Key Dates
    </h3>
    <div className="grid-3" data-test-id="key-dates-grid">
      <div data-test-id="origination-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.origination_date"
          data-test-id="origination-date-label"
        >
          Origination Date *
        </Label>
        <Controller
          name="dates.origination_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="origination-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="first-payment-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.first_payment_date"
          data-test-id="first-payment-date-label"
        >
          First Payment Date *
        </Label>
        <Controller
          name="dates.first_payment_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="first-payment-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="original-maturity-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.original_maturity_date"
          data-test-id="original-maturity-date-label"
        >
          Original Maturity Date *
        </Label>
        <Controller
          name="dates.original_maturity_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="original-maturity-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="prepayment-lockout-end-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.prepayment_lockout_end_date"
          data-test-id="prepayment-lockout-end-date-label"
        >
          Prepayment Lockout End Date
        </Label>
        <Controller
          name="dates.prepayment_lockout_end_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="prepayment-lockout-end-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="open-call-period-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.open_call_period_date"
          data-test-id="open-call-period-date-label"
        >
          Open Call Period Date
        </Label>
        <Controller
          name="dates.open_call_period_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="open-call-period-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="first-extension-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.first_extension_date"
          data-test-id="first-extension-date-label"
        >
          First Extension Date
        </Label>
        <Controller
          name="dates.first_extension_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="first-extension-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="first-extension-fee-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.first_extension_fee"
          data-test-id="first-extension-fee-label"
        >
          First Extension Fee
        </Label>
        <Controller
          name="dates.first_extension_fee"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="first-extension-fee-input"
            />
          )}
        />
      </div>
      <div data-test-id="second-extension-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.second_extension_date"
          data-test-id="second-extension-date-label"
        >
          Second Extension Date
        </Label>
        <Controller
          name="dates.second_extension_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="second-extension-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="second-extension-fee-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.second_extension_fee"
          data-test-id="second-extension-fee-label"
        >
          Second Extension Fee
        </Label>
        <Controller
          name="dates.second_extension_fee"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="second-extension-fee-input"
            />
          )}
        />
      </div>
      <div data-test-id="third-extension-date-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.third_extension_date"
          data-test-id="third-extension-date-label"
        >
          Third Extension Date
        </Label>
        <Controller
          name="dates.third_extension_date"
          control={control}
          render={({ field }) => (
            <DatePicker
              value={normalizeDateValue(field.value)}
              onChange={(val) => field.onChange(val ? new Date(val) : undefined)}
              data-test-id="third-extension-date-input"
            />
          )}
        />
      </div>
      <div data-test-id="third-extension-fee-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.third_extension_fee"
          data-test-id="third-extension-fee-label"
        >
          Third Extension Fee
        </Label>
        <Controller
          name="dates.third_extension_fee"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="third-extension-fee-input"
            />
          )}
        />
      </div>
      <div data-test-id="exit-fee-field">
        <Label
          className="form-label loanform-label"
          htmlFor="dates.exit_fee"
          data-test-id="exit-fee-label"
        >
          Exit Fee
        </Label>
        <Controller
          name="dates.exit_fee"
          control={control}
          render={({ field }) => (
            <USFormattedNumberInput
              value={field.value}
              onChange={(val) => field.onChange(val)}
              mode="percent"
              decimals={2}
              step={0.01}
              className="input-full loanform-input loanform-input-bg loanform-input-border"
              data-test-id="exit-fee-input"
            />
          )}
        />
      </div>
    </div>
  </div>
);
